import i18next, { t } from 'i18next';

export const getDirectionBackground = (direction) => {
    const background = {
        Long: 'bg-dashboard-lightGreen text-teal-600',
        Short: 'bg-dashboard-lightRed text-red'
    };

    return background[direction] || '';
};

export const sortPriceTargets = (targets) => {
    return targets.sort((a, b) => {
        return new Date(b.end || b.start) - new Date(a.end || a.start);
    });
};

export const blackweekItemActive = (variant = 'banner') => {
    const region = getRegion();

    if (region !== 'de') return false;

    const now = new Date();
    const earlyBird = new URLSearchParams(window.location.search).get('earlybird') === 'true';

    // For variant 'masterclass' always activate at 9 (no deactivation date).
    // For banner: early bird starts at 9, otherwise at 10, and deactivates on 3 Dec.
    const activationHour = variant === 'masterclass' ? 9 : earlyBird ? 9 : 10;

    const activationDate = new Date(2025, 10, 24, activationHour, 0, 0); // 24 Nov
    const deactivationDate = new Date(2025, 11, 3, 23, 59, 59); // 3 Dec

    return variant === 'masterclass' ? now >= activationDate : now >= activationDate && now <= deactivationDate;
};

export const adventCalendarActive = () => {
    const region = getRegion();

    if (region !== 'de') return false;

    const berlinNow = new Date(new Date().toLocaleString('en-US', { timeZone: 'Europe/Berlin' }));
    const year = berlinNow.getFullYear();

    const activationDateBerlin = new Date(new Date(year, 11, 1, 10, 0, 0).toLocaleString('en-US', { timeZone: 'Europe/Berlin' })); // 1 Dec
    const deactivationDateBerlin = new Date(new Date(year, 11, 24, 23, 59, 59).toLocaleString('en-US', { timeZone: 'Europe/Berlin' })); // 24 Dec

    return berlinNow >= activationDateBerlin && berlinNow <= deactivationDateBerlin;
};

export const formatCurrency = (value) => {
    return new Intl.NumberFormat('de-DE').format(value);
};

export const currencyData = {
    EUR: { symbol: '€', en: 'Euro', de: 'Euro', rate: null },
    CHF: { symbol: 'CHF', en: 'Swiss Franc', de: 'Schweizer Franken', rate: null },
    AUD: { symbol: 'A$', en: 'Australian Dollar', de: 'Australischer Dollar', rate: null },
    BGN: { symbol: 'лв', en: 'Bulgarian Lev', de: 'Bulgarischer Lew', rate: null },
    BRL: { symbol: 'R$', en: 'Brazilian Real', de: 'Brasilianischer Real', rate: null },
    CAD: { symbol: 'C$', en: 'Canadian Dollar', de: 'Kanadischer Dollar', rate: null },
    CNY: { symbol: '¥', en: 'Chinese Yuan', de: 'Chinesischer Yuan', rate: null },
    CZK: { symbol: 'Kč', en: 'Czech Koruna', de: 'Tschechische Krone', rate: null },
    DKK: { symbol: 'kr', en: 'Danish Krone', de: 'Dänische Krone', rate: null },
    GBP: { symbol: '£', en: 'British Pound', de: 'Britisches Pfund', rate: null },
    HKD: { symbol: 'HK$', en: 'Hong Kong Dollar', de: 'Hongkong-Dollar', rate: null },
    HUF: { symbol: 'Ft', en: 'Hungarian Forint', de: 'Ungarischer Forint', rate: null },
    IDR: { symbol: 'Rp', en: 'Indonesian Rupiah', de: 'Indonesische Rupiah', rate: null },
    ILS: { symbol: '₪', en: 'Israeli Shekel', de: 'Israelischer Schekel', rate: null },
    INR: { symbol: '₹', en: 'Indian Rupee', de: 'Indische Rupie', rate: null },
    ISK: { symbol: 'kr', en: 'Icelandic Krona', de: 'Isländische Krone', rate: null },
    JPY: { symbol: '¥', en: 'Japanese Yen', de: 'Japanischer Yen', rate: null },
    KRW: { symbol: '₩', en: 'South Korean Won', de: 'Südkoreanischer Won', rate: null },
    MXN: { symbol: 'Mex$', en: 'Mexican Peso', de: 'Mexikanischer Peso', rate: null },
    MYR: { symbol: 'RM', en: 'Malaysian Ringgit', de: 'Malaysischer Ringgit', rate: null },
    NOK: { symbol: 'kr', en: 'Norwegian Krone', de: 'Norwegische Krone', rate: null },
    NZD: { symbol: 'NZ$', en: 'New Zealand Dollar', de: 'Neuseeland-Dollar', rate: null },
    PHP: { symbol: '₱', en: 'Philippine Peso', de: 'Philippinischer Peso', rate: null },
    PLN: { symbol: 'zł', en: 'Polish Zloty', de: 'Polnischer Zloty', rate: null },
    RON: { symbol: 'lei', en: 'Romanian Leu', de: 'Rumänischer Leu', rate: null },
    SEK: { symbol: 'kr', en: 'Swedish Krona', de: 'Schwedische Krone', rate: null },
    SGD: { symbol: 'S$', en: 'Singapore Dollar', de: 'Singapur-Dollar', rate: null },
    THB: { symbol: '฿', en: 'Thai Baht', de: 'Thailändischer Baht', rate: null },
    TRY: { symbol: '₺', en: 'Turkish Lira', de: 'Türkische Lira', rate: null },
    USD: { symbol: '$', en: 'US Dollar', de: 'US-Dollar', rate: null },
    ZAR: { symbol: 'R', en: 'South African Rand', de: 'Südafrikanischer Rand', rate: null }
};

export const getCurrencySymbol = (asCode = false) => {
    if (asCode) {
        return window.meta.region_configuration.currency;
    }

    return window.meta.region_configuration.symbol;
};

export const isOriginalCurrency = () => {
    if (!window.meta || !window.meta.currency) return false;

    return window.meta.currency == getCurrencySymbol(true);
};

export const getCurrencyCode = () => {
    const currencyCode = window.meta && window.meta.currency ? window.meta.currency : getCurrencySymbol(true);

    return currencyCode || 'EUR';
};

export const toCurrency = (value, minimumFractionDigits = 2, useOrigCurrency = false) => {
    if (typeof value !== 'number') {
        return value;
    }

    const currencyCode = useOrigCurrency ? getCurrencySymbol(true) : getCurrencyCode();

    const rate = currencyData[currencyCode]?.rate || 1;
    value = value * rate;

    // set the locale based on the current language
    const language = window.meta?.languages?.vue?.language || 'de';
    const currentLocale = language === 'de' ? 'de-DE' : language === 'en' ? 'en-US' : language;

    // create a new formatter with the currency options
    const formatter = new Intl.NumberFormat(currentLocale, {
        style: 'currency',
        currency: currencyCode,
        minimumFractionDigits
    });

    // replace the currency code with the symbol
    const currencySymbol = currencyData[currencyCode]?.symbol || currencyCode;
    const formattedValue = formatter.format(value);

    if (formattedValue.includes(currencyCode)) {
        return formattedValue.replace(currencyCode, currencySymbol);
    }

    return formattedValue;
};

export const toPercent = (value, fixed = 2) => {
    if (typeof value !== 'number') {
        return value;
    }

    return (Math.round(value * 10000) / 100).toFixed(fixed) + '%';
};

export const asset = (path, storageType = 'public') => {
    if (storageType === 'public') {
        return window.meta.asset_url + path.replace(/^\/+/, '');
    } else if (storageType === 'storage') {
        return window.meta.storage_path + '/' + path.replace(/^\/+/, '');
    }
};

export const meta = () => {
    return window.meta;
};

export const hexToRgbA = (hex, opacity = 1) => {
    let c;

    if (/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
        c = hex.substring(1).split('');

        if (c.length == 3) {
            c = [c[0], c[0], c[1], c[1], c[2], c[2]];
        }

        c = '0x' + c.join('');

        return 'rgba(' + [(c >> 16) & 255, (c >> 8) & 255, c & 255].join(',') + ',' + opacity + ')';
    }

    throw new Error('Bad Hex');
};

export const scrollToId = (selector, yOffset = -100) => {
    const element = document.querySelector(selector);
    const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
    window.scrollTo({ top: y, behavior: 'smooth' });
};

export const onScreenPercentage = function (element) {
    let percentage = Math.round(((window.pageYOffset - 200) / (element.clientHeight - window.innerHeight)) * 100);

    percentage >= 100 ? (percentage = 100) : (percentage = percentage);

    percentage <= 0 ? (percentage = 0) : (percentage = percentage);

    return percentage;
};

export const countLines = (target) => {
    const style = window.getComputedStyle(target, null);
    let height = parseInt(style.getPropertyValue('height'));
    const font_size = parseInt(style.getPropertyValue('font-size'));
    let line_height = parseInt(style.getPropertyValue('line-height'));
    const box_sizing = style.getPropertyValue('box-sizing');

    if (isNaN(line_height)) line_height = font_size * 1.2;

    if (box_sizing == 'border-box') {
        const padding_top = parseInt(style.getPropertyValue('padding-top'));
        const padding_bottom = parseInt(style.getPropertyValue('padding-bottom'));
        const border_top = parseInt(style.getPropertyValue('border-top-width'));
        const border_bottom = parseInt(style.getPropertyValue('border-bottom-width'));
        height = height - padding_top - padding_bottom - border_top - border_bottom;
    }

    const lines = Math.ceil(height / line_height);

    return lines;
};

export const shuffleArray = (array) => {
    for (let i = array.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [array[i], array[j]] = [array[j], array[i]];
    }

    return array;
};

export const dragElement = (element) => {
    let pos1 = 0;
    let pos2 = 0;
    let pos3 = 0;
    let pos4 = 0;

    if (document.getElementById(element.id + 'header')) {
        if ('ontouchmove' in document.documentElement) {
            let draggableArea;
            const componentHeader = document.getElementById('contentheader');
            const component = document.getElementById('content');
            const mainContent = document.getElementById('mainContent');

            componentHeader.addEventListener('touchmove', function (e) {
                draggableArea = document.getElementById('draggableArea').getBoundingClientRect();

                const touchLocation = e.changedTouches[0];

                mainContent.classList.add('overflow-y-hidden');
                mainContent.classList.remove('overflow-y-auto');

                component.style.top = touchLocation.clientY - componentHeader.clientHeight / 2 + 'px';
                component.style.left = touchLocation.clientX - componentHeader.clientWidth / 2 + 'px';
            });

            componentHeader.addEventListener('touchend', function (e) {
                document.ontouchend = null;
                document.ontouchmove = null;

                mainContent.classList.add('overflow-y-auto');
                mainContent.classList.remove('overflow-y-hidden');

                if (component.getBoundingClientRect().top - draggableArea.top <= 0) {
                    component.style.top = '1px';
                }

                if (componentHeader.getBoundingClientRect().bottom - draggableArea.bottom >= 0) {
                    component.style.top = draggableArea.height - 100 + 'px';
                }

                if (component.getBoundingClientRect().right - draggableArea.right >= 0) {
                    component.style.left = draggableArea.width / 1.5 + 'px';
                }

                if (component.getBoundingClientRect().left - draggableArea.left <= 0) {
                    component.style.left = '1px';
                }
            });
        }

        document.getElementById(element.id + 'header').onmousedown = dragMouseDown;
    } else {
        // otherwise, move the DIV from anywhere inside the DIV:
        element.onmousedown = dragMouseDown;
    }

    function dragMouseDown(e) {
        e = e || window.event;
        e.preventDefault();
        // get the mouse cursor position at startup:
        pos3 = e.clientX;
        pos4 = e.clientY;
        document.onmouseup = closeDragElement;
        // call a function whenever the cursor moves:
        document.onmousemove = elementDrag;
    }

    function elementDrag(e) {
        e = e || window.event;
        e.preventDefault();
        // calculate the new cursor position:
        pos1 = pos3 - e.clientX;
        pos2 = pos4 - e.clientY;
        pos3 = e.clientX;
        pos4 = e.clientY;
        // set the element's new position:
        element.style.top = element.offsetTop - pos2 + 'px';
        element.style.left = element.offsetLeft - pos1 + 'px';
    }

    function closeDragElement() {
        // stop moving when mouse button is released:
        document.onmouseup = null;
        document.onmousemove = null;
    }
};

export const randomColor = (name) => {
    const letters = '0123456789ABCDEF';
    let color = '#';
    for (let i = 0; i < 6; i++) {
        color += letters[Math.floor(Math.random() * 16)];
    }

    return color;
};

export const getBackgroundColor = (productData) => {
    const categories = productData?.product?.productGroups.data;

    if (categories) {
        const categoryData = categories[0].productCategories.data;

        if (categoryData.length > 0) {
            const backgroundColor = categoryData[0].background_color || '#FFFFFF'; // Default color if none is found

            return lightenColor(backgroundColor, 0.9); // Lighten the color by 90%
        }
    }

    return '#FFFFFF'; // Default color if not found
};

export const shadeColor = (color, percent) => {
    let R = parseInt(color.substring(1, 3), 16);
    let G = parseInt(color.substring(3, 5), 16);
    let B = parseInt(color.substring(5, 7), 16);

    R = Math.floor((R * (100 + percent)) / 100);
    G = Math.floor((G * (100 + percent)) / 100);
    B = Math.floor((B * (100 + percent)) / 100);

    R = R < 255 ? R : 255;
    G = G < 255 ? G : 255;
    B = B < 255 ? B : 255;

    const RR = (R.toString(16).length == 1 ? '0' : '') + R.toString(16);
    const GG = (G.toString(16).length == 1 ? '0' : '') + G.toString(16);
    const BB = (B.toString(16).length == 1 ? '0' : '') + B.toString(16);

    return '#' + RR + GG + BB;
};

export const lightenColor = (hex, percent) => {
    // Remove the hash at the start if it's there
    hex = hex.replace(/^#/, '');

    // Parse the r, g, b values from the hex string
    let r = parseInt(hex.substring(0, 2), 16);
    let g = parseInt(hex.substring(2, 4), 16);
    let b = parseInt(hex.substring(4, 6), 16);

    // Calculate the amount to lighten
    r = Math.min(255, Math.round(r + (255 - r) * percent));
    g = Math.min(255, Math.round(g + (255 - g) * percent));
    b = Math.min(255, Math.round(b + (255 - b) * percent));

    // Convert back to hex and return
    return `#${((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1).toUpperCase()}`;
};

export const generateRandomBluePurpleTone = (index, totalSegments) => {
    const baseColors = [
        // {hue: 231, saturation: 71, lightness: 86}, // Base color 1
        // {hue: 238, saturation: 87, lightness: 60}, // Base color 2
        // {hue: 230, saturation: 95, lightness: 12}, // Base color 3
        { hue: 235, saturation: 80, lightness: 30 } // Base color 4
    ];

    // Determine which base color to start with based on index
    const colorIndex = Math.floor(index % baseColors.length);
    const baseColor = baseColors[colorIndex];

    // Calculate the lightness increment based on the total number of segments
    const maxLightnessVariation = 80; // Max variation in lightness (adjust as needed)
    const incrementPerSegment = maxLightnessVariation / totalSegments;

    // Adjust lightness for this segment
    const adjustedLightness = baseColor.lightness + incrementPerSegment * index;
    const lightness = Math.max(10, Math.min(90, adjustedLightness)); // Ensure it stays within bounds

    // Convert HSL to RGB
    const { r, g, b } = hslToRgb(baseColor.hue / 360, baseColor.saturation / 100, lightness / 100);

    // Convert RGB to HEX
    return rgbToHex(r, g, b);
};

const hslToRgb = (h, s, l) => {
    let r, g, b;

    if (s == 0) {
        r = g = b = l; // Grau
    } else {
        const hue2rgb = (p, q, t) => {
            if (t < 0) t += 1;

            if (t > 1) t -= 1;

            if (t < 1 / 6) return p + (q - p) * 6 * t;

            if (t < 1 / 2) return q;

            if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;

            return p;
        };

        const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
        const p = 2 * l - q;
        r = hue2rgb(p, q, h + 1 / 3);
        g = hue2rgb(p, q, h);
        b = hue2rgb(p, q, h - 1 / 3);
    }

    return {
        r: Math.round(r * 255),
        g: Math.round(g * 255),
        b: Math.round(b * 255)
    };
};

// RGB zu HEX Konvertierung
const rgbToHex = (r, g, b) => {
    return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1).toUpperCase()}`;
};

/**
 * Get the right market link for the customer dashboard, based in the product type.
 * @param {String} productType e.g. doubleProduct, bundleProduct
 * @param {String} groupId e.g 89a58eca-b424-4d11-a074-ddab5d2f8576
 * @returns {String}
 */
export function getMarketDashboardLink(productType, groupId) {
    switch (productType) {
        case 'doubleProduct':
            return '/dashboard/markets-double/' + groupId;
        case 'bundleProduct':
            return '/dashboard/markets-package/' + groupId;
        default:
            return '/dashboard/markets/' + groupId;
    }
}

/**
 * Helper to filter the product out of the product-category-dataset.
 * @param {String} productId
 * @param {Array} dataset
 * @returns {Object}
 */
export function filterProductCategoryBy(productId, dataset) {
    // TODO - check for data structure
    let result = null;

    if (dataset.length === undefined) {
        return result;
    }

    dataset.forEach((category) => {
        category.productGroups.data.filter((group) => {
            group.products.data.filter((product) => {
                if (product.id == productId) {
                    result = group;
                }
            });
        });
    });

    return result;
}

/**
 * Format the timestamp to a readable date
 * @param {String} timestamp
 * @returns {Date}
 */
export const formatTimestamp = (timestamp) => {
    const locale = window.meta.region_configuration.region === 'de' ? 'de-DE' : 'en-US';

    const formatOption = {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
    };

    return new Date(timestamp).toLocaleDateString(locale, formatOption);
};

export const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
};

export const getQueryParameterByName = (name, fallback) => {
    return new URLSearchParams(window.location.search).get(name) ?? fallback;
};

export const removeQueryParams = (params) => {
    _.forEach(params, (key) => {
        const url = new URL(window.location.href);

        url.searchParams.delete(key);
        window.history.replaceState(null, '', url);
    });
};

export const replaceQueryParams = (params) => {
    _.forEach(params, (param, key) => {
        const url = new URL(window.location.href);

        param != '' ? url.searchParams.set(key, param) : url.searchParams.delete(key);

        window.history.replaceState(null, '', url);
    });
};

export const formatDateByRegion = (date) => {
    if (!date) return null;

    date = window.meta.region_configuration.region === 'de' ? date.format('DD.MM.YYYY') : date.format('YYYY-MM-DD');

    return date;
};

export const formatPlainDateByRegion = (date) => {
    if (!date) return null;

    const region = window.meta.region_configuration.region;
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();

    return region === 'de' ? `${day}.${month}.${year}` : `${year}-${month}-${day}`;
};

export const formatDateTimeByRegion = (date) => {
    if (!date) return null;

    const locale = window.meta.region_configuration.region === 'de' ? 'de-DE' : 'en-US';
    const dateOptions = { day: 'numeric', month: 'numeric', year: 'numeric' };
    const timeOptions = { hour: '2-digit', minute: '2-digit', hour12: false };
    const d = new Date(date);
    const datePart = d.toLocaleDateString(locale, dateOptions);
    const timePart = d.toLocaleTimeString(locale, timeOptions);

    return `${datePart}, ${timePart}`;
};

function resolvePaymentMethodKey(paymentMethodData) {
    const { method, meta = {}, provider } = paymentMethodData;

    if (method === 'CREDITCARD') return meta.brand_name;

    if (method === 'REFERENCE_TRANSACTIONS') return provider;

    return method;
}

export function getImageFromPaymentMethod(paymentMethodData) {
    return resolvePaymentMethodKey(paymentMethodData).toLowerCase().replace(' ', '_');
}

export function getNameFromPaymentMethod(paymentMethodData) {
    return i18next.t(resolvePaymentMethodKey(paymentMethodData));
}

export const getRegion = () => {
    return window.meta.region_configuration.region;
};

export const convertToLastDay = (dateStr) => {
    const [month, year] = dateStr.split('/').map(Number);
    const lastDayDate = new Date(year, month, 0);
    const day = String(lastDayDate.getDate()).padStart(2, '0');
    const mm = String(lastDayDate.getMonth() + 1).padStart(2, '0');
    const yyyy = lastDayDate.getFullYear();

    return new Date(`${yyyy}.${mm}.${day}`);
};
